import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";

import { employeesApi } from "@/lib/api/employees";
import { notify } from "@/lib/notify";
import { qk } from "@/lib/query-client";
import { can } from "@/stores/auth-store";

export const EMPLOYEE_LIMIT_TITLE = "Employee limit reached";

export function employeeLimitMessage(limit: number): string {
  const noun = limit === 1 ? "employee" : "employees";
  return `Your company has reached its limit of ${limit} ${noun}, so no more can be added right now. Please contact your HRMS provider to increase the limit.`;
}

/**
 * The company's limit when it has been reached, otherwise undefined. An early
 * notice only: EmployeeService.checkHeadcount is the real limit, so a failed
 * read, or a role that can't read the headcount, leaves the way open. Always
 * refetched, because the dashboard shares this cached reading and the limit may
 * have been raised since.
 */
export function useReachedEmployeeLimit(enabled = true): number | undefined {
  const { data } = useQuery({
    queryKey: qk.employees.headcount,
    queryFn: () => employeesApi.headcount(),
    enabled: enabled && can("employee.read"),
    staleTime: 0,
  });
  const limit = data?.max_employees;
  return data && limit && data.on_roll >= limit ? limit : undefined;
}

/**
 * For an Add Employee button: returns a check that shows the limit popup and
 * answers true when the button should stay put.
 */
export function useEmployeeLimitGuard(enabled = true): () => boolean {
  const limit = useReachedEmployeeLimit(enabled);
  return useCallback(() => {
    if (limit === undefined) return false;
    notify.alert({ title: EMPLOYEE_LIMIT_TITLE, message: employeeLimitMessage(limit) });
    return true;
  }, [limit]);
}
